import * as Dialog from "@radix-ui/react-dialog";
import { PlainDate, getCurrentDate } from "@sa-nu/shared-utils/date";
import { assertNever } from "@sa-nu/shared-utils/misc";
import clsx from "clsx";
import Image from "next/image";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import { useGtm } from "../../../../lib/google-tag-manager/gtm";
import { RangeCalendar } from "../../ui/range-calendar";

import type {
  CICOSelection,
  ReservationCalendarNavigationSize,
} from "./reservation-calendar";

type CICOModalActions = {
  open: () => void;
};

const CICOModalContext = createContext<CICOModalActions | null>(null);

export function useCICOModal() {
  const ctx = useContext(CICOModalContext);
  if (!ctx)
    throw new Error(
      "useCICOModal must be used within CICODateSelectionModalProvider",
    );
  return ctx;
}

// Providerの中にOpen状態を閉じ込め、Contextで渡すActionを安定にすることで、
// 子コンポーネントの余計なレンダリングが発生しないようにしている。
// 他のモーダルはCustom Hooks + render funcパターンで実装しているが、
// これは子コンポーネントの余計なレンダリングを誘発するので、
// こちらのパターンで実装した方が良い。
export function CICODateSelectionModalProvider({
  cico,
  onConfirm,
  onClear,
  onJump,
  minNight,
  maxNight,
  navigationSize,
  firstNight,
  children,
}: {
  cico: CICOSelection | null;
  onConfirm: (cico: CICOSelection) => void;
  onClear: () => void;
  onJump: (date: PlainDate) => void;
  minNight: PlainDate;
  maxNight: PlainDate;
  navigationSize: ReservationCalendarNavigationSize;
  firstNight: PlainDate;
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [filterMode, setFilterMode] = useState(true);

  const actions = useMemo(
    () => ({
      open: () => {
        setIsOpen(true);
      },
    }),
    [],
  );

  const handleConfirm = useCallback(
    (cico: CICOSelection) => {
      onConfirm(cico);
      setIsOpen(false);
    },
    [onConfirm],
  );

  const handleClear = useCallback(() => {
    onClear();
  }, [onClear]);

  const handleJump = useCallback(
    (date: PlainDate) => {
      onJump(date);
      setIsOpen(false);
    },
    [onJump],
  );

  return (
    <CICOModalContext.Provider value={actions}>
      {children}
      {isOpen && (
        <CICODateSelectionModalContent
          cico={cico}
          open={isOpen}
          onOpenChange={setIsOpen}
          minNight={minNight}
          maxNight={maxNight}
          navigationSize={navigationSize}
          firstNight={firstNight}
          filterMode={filterMode}
          onFilterModeChange={setFilterMode}
          onConfirm={handleConfirm}
          onClear={handleClear}
          onJump={handleJump}
        />
      )}
    </CICOModalContext.Provider>
  );
}

// idle: 未選択 → 次のタップでCIを設定
// selectingCheckOut: CI設定済み → 次のタップでCOを設定
// confirmed: CI/CO両方設定済み → 次のタップで新たなCIとしてやり直し
type SelectionPhase = "idle" | "selectingCheckOut" | "confirmed";

function CICODateSelectionModalContent({
  cico,
  open,
  onOpenChange,
  minNight,
  maxNight,
  navigationSize,
  firstNight,
  filterMode,
  onFilterModeChange,
  onConfirm,
  onClear,
  onJump,
}: {
  cico: CICOSelection | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  minNight: PlainDate;
  maxNight: PlainDate;
  navigationSize: ReservationCalendarNavigationSize;
  firstNight: PlainDate;
  filterMode: boolean;
  onFilterModeChange: (value: boolean) => void;
  onConfirm: (cico: CICOSelection) => void;
  onClear: () => void;
  onJump: (date: PlainDate) => void;
}) {
  const today = getCurrentDate();
  const gtm = useGtm();
  const [phase, setPhase] = useState<SelectionPhase>(
    cico ? "confirmed" : "idle",
  );
  const [checkInDraft, setCheckInDraft] = useState<PlainDate | null>(
    cico?.checkIn ?? null,
  );
  const [checkOutDraft, setCheckOutDraft] = useState<PlainDate | null>(
    cico?.checkOut ?? null,
  );

  const handleDateClick = (date: PlainDate) => {
    if (!filterMode) {
      onJump(date);
      return;
    }
    switch (phase) {
      case "idle":
      case "confirmed":
        // 1タップ目 or 3タップ目: チェックインとして設定
        setCheckInDraft(date);
        setCheckOutDraft(null);
        setPhase("selectingCheckOut");
        break;

      case "selectingCheckOut": {
        // 2タップ目
        if (checkInDraft === null) return; // should not be here

        const cmp = PlainDate.compare(date, checkInDraft);
        if (cmp === 0) return; // CI = CO なら何も起こらない
        if (cmp > 0) {
          // CIよりCOの方が後 → 正しいのでチェックアウトとして扱う
          const nights = checkInDraft.until(date).total({ unit: "day" });
          if (nights > navigationSize) return;
          setCheckOutDraft(date);
          setPhase("confirmed");
        } else {
          // CIよりCOの方が手前 → その日をチェックインとしてやり直し
          setCheckInDraft(date);
          setCheckOutDraft(null);
        }
        break;
      }
      default:
        assertNever(phase);
    }
  };

  const handleConfirm = () => {
    if (!checkInDraft) return;
    // CIのみ選択していた場合は1泊扱いとする（おもてなし）
    const checkOut = checkOutDraft ?? checkInDraft.add({ days: 1 });
    gtm.sendEvent("rsv_cal_cico_date_set");
    onConfirm({ checkIn: checkInDraft, checkOut });
  };

  const handleClear = () => {
    setCheckInDraft(null);
    setCheckOutDraft(null);
    setPhase("idle");
    onClear();
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          // モーダルを閉じる時にステートをリセット
          setCheckInDraft(null);
          setCheckOutDraft(null);
          setPhase("idle");
        }
        onOpenChange(nextOpen);
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="sd-overlay" />
        <Dialog.Content
          className={clsx(
            "fixed top-1/2 left-1/2 z-50 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-[30px]",
            "min-w-[310px]",
          )}
          aria-describedby={undefined}
        >
          <Dialog.Title className="sr-only">日付を選択</Dialog.Title>

          <div className="flex max-h-[85vh] w-full flex-col overflow-hidden bg-white">
            <div className="flex flex-1 flex-col gap-[8px] overflow-y-auto p-[16px]">
              <div
                role="tablist"
                className="bg-skogecha-4 flex h-[40px] items-center justify-center self-center rounded-[6px] p-[4px]"
              >
                {(
                  [
                    { label: "期間で検索", value: true },
                    { label: "日付を選択", value: false },
                  ] as const
                ).map(({ label, value }) => (
                  <button
                    data-analytics-id="reservation_calendar_selected_search_mode"
                    key={label}
                    type="button"
                    role="tab"
                    aria-selected={filterMode === value}
                    onClick={() => onFilterModeChange(value)}
                    className={clsx(
                      "font-notojp flex shrink-0 items-center justify-center rounded-[4px] px-[12px] py-[6px] text-[14px] leading-[20px] font-medium",
                      filterMode === value
                        ? "text-sorange bg-white shadow-[0px_1px_3px_0px_rgba(0,0,0,0.05)]"
                        : "text-skogecha-60",
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <RangeCalendar
                min={minNight}
                max={maxNight}
                checkIn={filterMode ? checkInDraft : null}
                checkOut={filterMode ? checkOutDraft : null}
                onDateClick={handleDateClick}
                today={today}
                initialMonth={firstNight}
              />
            </div>

            {/* Footer */}
            {filterMode && (
              <div className="border-skogecha/10 flex items-center gap-[8px] border-t px-[16px] py-[8px] md:py-[12px]">
                <button
                  data-analytics-id="reservation_calendar_clicked_date_range_clear"
                  onClick={handleClear}
                  className="text-skogecha flex shrink-0 items-center justify-center rounded-full border border-[#c2c2c2] bg-white px-[24px] py-[12px] text-[14px] leading-none font-bold"
                >
                  クリア
                </button>
                <button
                  data-analytics-id="reservation_calendar_searched_date_range"
                  disabled={!checkInDraft}
                  onClick={handleConfirm}
                  className="bg-sorange flex flex-1 items-center justify-center rounded-[100px] py-[12px] text-[14px] leading-none font-bold text-white disabled:opacity-50"
                >
                  この日程の空きを表示
                </button>
              </div>
            )}
          </div>

          {/* Close button */}
          <Dialog.Close
            className="flex size-[40px] items-center justify-center rounded-full bg-white"
            aria-label="モーダルを閉じる"
          >
            <Image src="/i/icon_close_x.svg" alt="" width={16} height={16} />
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
